import { useApolloClient } from "@apollo/client";
import { wizard } from "@msys/question-isomorphic";
import { useSnackbar } from "notistack";
import React from "react";
import { ApplyItemActionInput } from "../../../../clients/graphqlTypes.js";
import { WizardQuestions_QuestionFragment } from "../../documents/WizardQuestions.generated.js";
import { useQuoteWizard_ApplyQuoteItemActionsMutation } from "./WizardQuestions.generated.js";

/**
 * Persists one answer and moves on.
 *
 * The answer goes through `applyQuoteItemActions`, the same mutation every
 * other surface uses, so nothing about the answer is held client-side. Then
 * both the question list and the open document queries are refetched — an
 * answer can add or eliminate whole subtrees, and the view has no blast radius
 * to go by — and the next question is resolved against the fresh list by
 * identity, which is why the pre-answer list is passed in too. The questions
 * left behind are recorded across refetches, because a later answer can hide a
 * passed question and the lists alone could no longer name it when it returns.
 */
export function useAnswerWizardQuestion({
  projectId,
  quoteId,
  wizardContext,
  questions,
  refetchQuestions,
  onStep,
}: {
  projectId: string;
  quoteId: string;
  wizardContext: string;
  questions: ReadonlyArray<WizardQuestions_QuestionFragment>;
  refetchQuestions: () => Promise<Array<WizardQuestions_QuestionFragment>>;
  onStep: (question: WizardQuestions_QuestionFragment | null) => void;
}) {
  const client = useApolloClient();
  const { enqueueSnackbar } = useSnackbar();
  const [stepping, setStepping] = React.useState(false);
  const passedQuestionIds = React.useRef(new Set<string>());

  React.useEffect(() => {
    passedQuestionIds.current = new Set();
  }, [projectId, quoteId, wizardContext]);
  const [applyQuoteItemActions, { loading }] =
    useQuoteWizard_ApplyQuoteItemActionsMutation({ client });

  // Resolves the question after `questionId` once its answer is persisted. The
  // placeholder panels persist through their own mutations and call this too.
  const stepAfterAnswer = React.useCallback(
    async (questionId: string) => {
      const previousQuestions = questions;
      const currentQuestionIndex = previousQuestions.findIndex(
        q => q.questionId === questionId
      );

      for (const id of wizard.passedQuestionIdsOnAnswer({
        questions: previousQuestions,
        currentQuestionIndex,
      })) {
        passedQuestionIds.current.add(id);
      }
      passedQuestionIds.current.add(questionId);

      setStepping(true);
      try {
        const [freshQuestions] = await Promise.all([
          refetchQuestions(),
          // The error link already reports these; an unrelated query failing
          // must not hold up the step.
          client.reFetchObservableQueries().catch(() => undefined),
        ]);

        const step = wizard.resolveStepAfterAnswer({
          previousQuestions,
          freshQuestions,
          currentQuestionIndex,
          passedQuestionIds: passedQuestionIds.current,
        });

        onStep(
          step.type === "goto" ? (freshQuestions[step.index] ?? null) : null
        );
      } catch (error) {
        // The answer is already saved; the question stays open.
        if (error instanceof Error)
          enqueueSnackbar(error.message, { variant: "error" });
      } finally {
        setStepping(false);
      }
    },
    [client, enqueueSnackbar, onStep, questions, refetchQuestions]
  );

  const answer = React.useCallback(
    async (questionId: string, action: ApplyItemActionInput) => {
      try {
        await applyQuoteItemActions({
          variables: {
            input: { projectId, quoteId, applyItemActions: [action] },
          },
        });
      } catch (error) {
        // The question stays open with the user's input intact.
        if (error instanceof Error)
          enqueueSnackbar(error.message, { variant: "error" });
        return;
      }

      await stepAfterAnswer(questionId);
    },
    [
      applyQuoteItemActions,
      enqueueSnackbar,
      projectId,
      quoteId,
      stepAfterAnswer,
    ]
  );

  return [answer, loading || stepping, stepAfterAnswer] as const;
}
